import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidatorFn, AbstractControl, ValidationErrors, FormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from '@env/environment';
import { ConfirmationDialogueComponent } from 'app/components/common/confirmation-dialogue/confirmation-dialogue.component';
import { IAccountAffiliation } from 'app/models/common/account-affiliation';
import { IAddress } from 'app/models/common/address';
import { IContactInformation } from 'app/models/common/contact-information';
import { IDocumentFile } from 'app/models/document/document-file';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
  }),
  withCredentials: true
};

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private api: string = environment.privateApi;
  loaded: boolean = false;
  accountAffiliations: IAccountAffiliation[];
  firmAccountAffiliations: IAccountAffiliation[];

  // Filter memory for ATP Management
  atpManagementFilter: {
    filterString: string;
    filteredActive: FormControl;
    filterDiscipline: FormControl;
    expirationStartDate: FormControl;
    expirationEndDate: FormControl;
  } = {
    filterString: '',
    filteredActive: new FormControl(null),
    filterDiscipline: new FormControl(null),
    expirationStartDate: new FormControl(null),
    expirationEndDate: new FormControl(null)
  };

  // Filter memory for ATP Application List (reusing name from firm)
  atpApplicationFilter: {
    filterString: string;
    selectedStatus: FormControl;
    selectedType: FormControl;
    applicationDateFilterStart: Date | null;
    applicationDateFilterEnd: Date | null;
    selectedRenewal: FormControl;
  } = {
    filterString: '',
    selectedStatus: new FormControl(['In Progress', 'Submitted', 'In Review', 'Action Needed']),
    selectedType: new FormControl([]),
    applicationDateFilterStart: null,
    applicationDateFilterEnd: null,
    selectedRenewal: new FormControl('')
  };

  firmManagementFilter: {
    filterString: string;
    filteredActive: FormControl;
    expirationStartDate: FormControl;
    expirationEndDate: FormControl;
    selectedCertificateStatuses: FormControl;
  } = {
    filterString: '',
    filteredActive: new FormControl('Both'),
    expirationStartDate: new FormControl(null),
    expirationEndDate: new FormControl(null),
    selectedCertificateStatuses: new FormControl([])
  };

  firmApplicationFilter: {
    filterString: string;
    selectedStatus: FormControl;
    selectedType: FormControl;
    applicationDateFilterStart: Date | null;
    applicationDateFilterEnd: Date | null;
  } = {
    filterString: '',
    selectedStatus: new FormControl(['In Progress', 'Submitted', 'In Review', 'Action Needed', 'Expired']),
    selectedType: new FormControl([]),
    applicationDateFilterStart: null,
    applicationDateFilterEnd: null,
  };

  individualManagementFilter: {
    filterString: string;
    filteredActive: FormControl;
    expirationStartDate: FormControl;
    expirationEndDate: FormControl;
    disciplineTypes: FormControl;
  } = {
    filterString: '',
    filteredActive: new FormControl('Active'),
    expirationStartDate: new FormControl(null),
    expirationEndDate: new FormControl(null),
    disciplineTypes: new FormControl([])
  };

  individualApplicationFilter: {
    filterString: string;
    selectedStatus: FormControl;
    selectedType: FormControl;
    applicationDateFilterStart: Date | null;
    applicationDateFilterEnd: Date | null;
  } = {
    filterString: '',
    selectedStatus: new FormControl(['In Progress', 'Submitted', 'In Review', 'Action Needed', 'Expired']),
    selectedType: new FormControl([]),
    applicationDateFilterStart: null,
    applicationDateFilterEnd: null,
  };

  constructor(private toastr: ToastrService, 
    private dialog: MatDialog,
    private http?: HttpClient,) 
  { 
    this.loadAccountAffiliations()
  }

  loadAccountAffiliations(){
    this.loaded = false;
    this.getAccountAffiliation().subscribe(res=>{
      this.accountAffiliations = res;
      this.getAllFirmAccountAffiliations().subscribe(res=>{
        this.firmAccountAffiliations = res;
        this.loaded = true;
      })      
    },err=>{})    
  }

  getAccountAffiliation(): Observable<IAccountAffiliation[]> {    
    return this.http.get<IAccountAffiliation[]>(environment.privateApi + 'Common/GetAccountAffiliation/', { withCredentials: true });
  }

  getAllFirmAccountAffiliations(): Observable<IAccountAffiliation[]> {    
    return this.http.get<IAccountAffiliation[]>(environment.privateApi + 'Common/GetAllFirmAccountAffiliations/', { withCredentials: true });
  }

  saveAccountAffiliation(accountAffiliation: IAccountAffiliation): Observable<IAccountAffiliation> {    
    return this.http.post<IAccountAffiliation>(this.api + 'Common/SaveAccountAffiliation', accountAffiliation, httpOptions);
  }

  saveDocumentFile(document: IDocumentFile): Observable<IDocumentFile> {    
    return this.http.post<IDocumentFile>(this.api + 'Common/SaveDocumentFile', document, httpOptions);
  }

  deleteDocumentFile(id: number): Observable<any> {    
    return this.http.get(this.api + 'Common/DeleteDocumentFile/' + id, httpOptions);
  }

  getDocumentFile(id: number, fileName?: string): Observable<IDocumentFile> {
    const params = fileName ? `?fileName=${encodeURIComponent(fileName)}` : '';
    return this.http.get<IDocumentFile>(this.api + 'Common/GetDocumentFile/' + id + params, { withCredentials: true });
  }

  getDocumentFileStream(id: number, fileName?: string): Observable<Blob> {
    const params = fileName ? `?fileName=${encodeURIComponent(fileName)}` : '';
    return this.http.get(this.api + 'Common/GetDocumentFileStream/' + id + params, { 
      responseType: 'blob',
      withCredentials: true 
    });
  }

  openDialog(message: string, title: string): MatDialogRef<ConfirmationDialogueComponent> {
    return this.dialog.open(ConfirmationDialogueComponent, {
      width: '400px',
      data: {message,title},
    });
  }

  emailValidator(emailControlName: string, confirmEmailControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const email = control.get(emailControlName);
      const confirmEmail = control.get(confirmEmailControlName);

      if (!email || !confirmEmail) {      
        return null;
      }

      if (confirmEmail.errors && !confirmEmail.errors['emailMismatch']) {      
        return null;
      }

      if (email.value.toLowerCase() !== confirmEmail.value.toLowerCase()) {
        confirmEmail.setErrors({ emailMismatch: true });
        return { emailMismatch: true }; // Return error object with emailMismatch key.
      } else {      
        confirmEmail.setErrors(null);
        return null; // No errors.
      }
    };
  }

  async getFileSerializedData(file: File): Promise<{ serializedData: string, fileType: string }> {
    return new Promise<{ serializedData: string, fileType: string }>((resolve, reject) => {
      const reader = new FileReader();
  
      reader.onload = (event: any) => {
        if (event.target) {
          const arrayBuffer = event.target.result;
          if (arrayBuffer instanceof ArrayBuffer) {
            const serializedData = this.base64ArrayBuffer(arrayBuffer);
            resolve({ serializedData, fileType: file.type });
          } else {
            reject(new Error("Failed to read the file."));
          }
        } else {
          reject(new Error("Failed to read the file."));
        }
      };
  
      reader.readAsArrayBuffer(file);
    });
  }  

  base64ArrayBuffer(arrayBuffer: ArrayBuffer): string {
    const byteArray = new Uint8Array(arrayBuffer);
    const byteCharacters = Array.from(byteArray);
    return btoa(byteCharacters.map(charCode => String.fromCharCode(charCode)).join(''));
  }

  async undoFileSerialization(serializedData: string, fileName: string, fileType: string): Promise<File> {
    return new Promise<File>((resolve, reject) => {
      try {
        const byteCharacters = atob(serializedData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: fileType });
        const file = new File([blob], fileName, { type: fileType });
        resolve(file);
      } catch (error) {
        reject(error);
      }
    });
  } 
  
  downloadFile(row: any) {
    this.getDocumentFile(row.document.id).subscribe(result=>{
      row.document = result;      
      this.convertFileAndDownload(row);
    },error=>{this.toastr.error("Error getting document file for download: ", error)});
  }

  downloadFileStream(row: any): void {    
    this.getDocumentFileStream(row.document.id).subscribe((blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = row.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }, error => {
      console.error('Error downloading file', error);
    });
  }

  async convertFileAndDownload(row: any) {            
    let rawData;
    let fileName;
    let fileType;
            
    rawData = row.document.file;
    fileName = row.fileName;
    fileType = row.fileType;

    try {
      const file = await this.undoFileSerialization(rawData, fileName, fileType);
  
      if (file) {        
        const fileUrl = URL.createObjectURL(file);
          
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = file.name; 
        a.style.display = 'none';
  
        document.body.appendChild(a);
        a.click();
          
        document.body.removeChild(a);
        URL.revokeObjectURL(fileUrl);
      } else {
        this.toastr.error("File object is null; unable to download.");
      }
    } catch (error) {
      this.toastr.error("Error undoing serialization:", error);
    }
  }  

  saveAddress(address: IAddress): Observable<any> {
    return this.http.post(environment.privateApi + 'Common/SaveAddress', address, httpOptions);
  }

  saveContactInformation(contactInformation: IContactInformation): Observable<IContactInformation> {    
    return this.http.post<IContactInformation>(this.api + 'Common/SaveContactInformation', contactInformation, httpOptions);
  }
}
